import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import { TableColumn } from "@models/components/tableColumn";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { PortfolioExternalAccount } from "@models/clients/portfolioExternalAccount";
import { extractErrorMessage } from "@common/utils/errorUtils";

export class ClientDetailsAccountReferenceController {
    public client: any;
    public isViewOnly: boolean = false;
    public mcbAccountCount: number = 12; // Number of MCB accounts
    public cdsAccountCount: number = 12; // Number of CDS accounts
    public edrAccountCount: number = 12; // Number of EDR accounts
    public afrasiaAccountCount: number = 3; // Number of Afrasia accounts

    // PortfolioExternalAccount properties
    public portfolioExternalAccounts: PortfolioExternalAccount[] = [];
    public loading: boolean = true;
    public dropdownOptions: any = {};

    // Reusable table properties for portfolio external accounts
    public portfolioExternalAccountColumns: TableColumn[] = [
        { key: 'accountNumber', label: 'Account Number', type: 'text' },
        { key: 'accountSourceName', label: 'Account Source', type: 'text' },
        { key: 'tradeAccountNumber', label: 'Trade account (CDS)', type: 'text' },
        { key: 'isActive', label: 'Active', type: 'boolean'}
    ];

    public portfolioExternalAccountActions = [
        {
            icon: 'edit',
            cssClass: '',
            title: 'Edit Portfolio External Account',
            onClick: (item: any, index: number) => this.editPortfolioExternalAccount(item, index)
        }
    ];

    /** @ngInject */
    constructor(
        private clientDetailsService: ClientDetailsService,
        private $mdDialog: angular.material.IDialogService,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private dropdownOptionsService: DropdownOptionsService,
        private clientCRMRepo: ClientCRMRepo,
        private notifyjs: any
    ) {}

    public $onInit(): void {
        // Component initialization logic
        this.setupClientWatcher();
        
        // Load dropdown options
        this.dropdownOptionsService.getAllOptions()
            .then((options) => {
                this.loadDropdownOptions(options);
            })
            .catch((error) => {
                console.warn('Failed to get dropdown options:', error);
            });
    }

    private setupClientWatcher(): void {
        // Watch for changes to the client object
        this.$rootScope.$watch(() => this.client, (newClient) => {
            if (newClient && newClient.id) {
                this.loadPortfolioExternalAccounts();
            } 
        }, true); // Deep watch
    }

    public loadPortfolioExternalAccounts(): void {
        if (this.client && this.client.id) {
            this.loading = true;
            this.clientCRMRepo.getPortfolioExternalAccounts(this.client.id)
                .then((response: any) => {
                    this.portfolioExternalAccounts = response.data || [];
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Error loading portfolio external accounts:', error);
                    this.loading = false;
                });
        }
    }

    public addPortfolioExternalAccount(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.portfolioExternalAccount = {
            selected: false
        };
        
        // Pass dropdown options to the modal
        tempScope.dropdownOptions = this.dropdownOptions;
        tempScope.existingAccounts = this.portfolioExternalAccounts;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="50"><add-portfolio-external-account-modal client="client" portfolio-external-account="portfolioExternalAccount" dropdown-options="dropdownOptions" existing-accounts="existingAccounts"></add-portfolio-external-account-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.loadPortfolioExternalAccounts();
                }
            })
            .catch((error) => {
                console.error('Error opening portfolio external account modal:', error);
            });
    }

    public editPortfolioExternalAccount(item: any, index: number): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.portfolioExternalAccount = item;
        tempScope.dropdownOptions = this.dropdownOptions;
        tempScope.existingAccounts = this.portfolioExternalAccounts;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="50"><add-portfolio-external-account-modal client="client" portfolio-external-account="portfolioExternalAccount" dropdown-options="dropdownOptions" existing-accounts="existingAccounts"></add-portfolio-external-account-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.loadPortfolioExternalAccounts();
                }
            })
            .catch((error) => {
                console.error('Error opening portfolio external account modal:', error);
            });
    }

    /** Returns a promise so the reusable table can await and show success or the API error message. */
    public deleteSelectedPortfolioExternalAccounts(itemIds: number[]): angular.IPromise<void> | void {
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Portfolio External Accounts')
            .textContent(`Are you sure you want to delete ${itemIds.length} portfolio external account(s)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            this.loading = true;
            return this.clientCRMRepo.deletePortfolioExternalAccounts(itemIds)
                .then(() => {
                    this.loadPortfolioExternalAccounts();
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Error deleting portfolio external accounts:', error);
                    this.loading = false;
                    throw error; // Reject so the table shows the error message
                });
        });
    }

    private loadDropdownOptions(options: any): void {
        // Store all options in the single dropdownOptions object
        this.dropdownOptions = options;
    }

    public getMcbAccountNumbers(): number[] {
        return Array.from({length: this.mcbAccountCount}, (_, i) => i + 1);
    }

    public getCdsAccountNumbers(): number[] {
        return Array.from({length: this.cdsAccountCount}, (_, i) => i + 1);
    }

    public getEdrAccountNumbers(): number[] {
        return Array.from({length: this.edrAccountCount}, (_, i) => i + 1);
    }

    public getAfrasiaAccountNumbers(): number[] {
        return Array.from({length: this.afrasiaAccountCount}, (_, i) => i + 1);
    }

    public isIndividual(): boolean {
        return this.clientDetailsService.isIndividual(this.client);
    }

    public isInstitution(): boolean {
        return this.clientDetailsService.isInstitution(this.client);
    }
}

export const ClientDetailsAccountReferenceComponent: angular.IComponentOptions = {
    template: require('./clientdetails.account-reference.html'),
    controller: ClientDetailsAccountReferenceController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/account-reference/clientdetails.account-reference.ts